import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ESTIMATE_MINUTES_OPTIONS,
  estimateMinutesForSelect,
  formatEstimateLabel,
} from '../../utils/estimateTime';
import { loadOrgSpaces } from '../../store/slices/spaceSlice';

/**
 * Compact title + estimate-time row for the pending task list.
 */
const QuickTaskComposer = ({
  placeholder = 'Task title',
  submitLabel = 'Add',
  saving = false,
  initialTitle = '',
  initialMinutes = 15,
  compact = false,
  flush = false,
  persistent = false,
  stacked = false,
  autoFocus = true,
  assignees = [],
  defaultAssigneeId = '',
  onSubmit,
  onCancel,
  showSpace = false,
}) => {
  const dispatch = useDispatch();
  const spaces = useSelector((state) => state.spaces.list || []);
  const spacesFetchedOnce = useSelector((state) => Boolean(state.spaces.fetchedOnce));
  const [title, setTitle] = useState(initialTitle);
  const [minutes, setMinutes] = useState(String(initialMinutes || 15));
  const [assigneeId, setAssigneeId] = useState(String(defaultAssigneeId || ''));
  const [spaceId, setSpaceId] = useState('');
  const inputRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    setTitle(initialTitle || '');
    setMinutes(String(initialMinutes || 15));
  }, [initialTitle, initialMinutes]);

  useEffect(() => {
    if (!assignees.length) return;
    setAssigneeId((current) => {
      if (current && assignees.some((user) => String(user.id) === String(current))) {
        return current;
      }
      if (defaultAssigneeId && assignees.some((user) => String(user.id) === String(defaultAssigneeId))) {
        return String(defaultAssigneeId);
      }
      return String(assignees[0].id);
    });
  }, [assignees, defaultAssigneeId]);

  useEffect(() => {
    if (!showSpace || spacesFetchedOnce) return undefined;
    dispatch(loadOrgSpaces());
    return undefined;
  }, [showSpace, spacesFetchedOnce, dispatch]);

  useEffect(() => {
    if (!autoFocus) return;
    inputRef.current?.focus();
    inputRef.current?.select?.();
  }, [autoFocus]);

  useEffect(() => {
    if (persistent) return undefined;
    const isInsideEditor = (target) => {
      if (!target) return false;
      if (formRef.current?.contains(target)) return true;
      const tag = target.tagName;
      return tag === 'OPTION' || tag === 'SELECT';
    };

    const handlePointerDown = (event) => {
      if (saving) return;
      if (isInsideEditor(event.target)) return;
      onCancel?.();
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) {
        event.preventDefault();
        onCancel?.();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onCancel, saving, persistent]);

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    const nextTitle = title.trim();
    if (!nextTitle || saving) return;
    const estimateTimeInMinutes = Number(minutes);
    await onSubmit?.({
      title: nextTitle,
      estimateTimeInMinutes: Number.isFinite(estimateTimeInMinutes) && estimateTimeInMinutes > 0
        ? estimateTimeInMinutes
        : 15,
      ...(assignees.length > 0 ? { assigneeUserId: assigneeId || defaultAssigneeId || null } : {}),
      ...(showSpace ? {
        spaceId,
        spaceName: (spaces.find((space) => space.id === spaceId) || {}).name || '',
      } : {}),
    });
    if (persistent) {
      setTitle('');
      setMinutes(String(initialMinutes || 15));
      setAssigneeId(String(defaultAssigneeId || ''));
      setSpaceId('');
      inputRef.current?.focus();
    }
  };

  const minuteOptions = estimateMinutesForSelect(initialMinutes);
  const timeSelect = (
    <select
      value={minutes}
      onChange={(e) => setMinutes(e.target.value)}
      disabled={saving}
      aria-label="Time taken"
      className="h-8 min-w-0 flex-1 px-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
    >
      {(minuteOptions.length ? minuteOptions : ESTIMATE_MINUTES_OPTIONS).map((m) => (
        <option key={m} value={m}>{formatEstimateLabel(m)}</option>
      ))}
    </select>
  );
  const assigneeSelect = assignees.length > 0 ? (
    <select
      value={assigneeId}
      onChange={(e) => setAssigneeId(e.target.value)}
      disabled={saving}
      aria-label="Assign to"
      title="Assign to"
      className="h-8 min-w-0 flex-1 px-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
    >
      {assignees.map((user) => (
        <option key={user.id} value={user.id}>{user.label}</option>
      ))}
    </select>
  ) : null;
  const spaceSelect = showSpace ? (
    <select
      value={spaceId}
      onChange={(e) => setSpaceId(e.target.value)}
      disabled={saving}
      aria-label="Space"
      title="Space"
      className="h-8 min-w-0 flex-1 px-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
    >
      <option value="">Space</option>
      {spaces.map((space) => (
        <option key={space.id} value={space.id}>{space.name}</option>
      ))}
    </select>
  ) : null;
  const actionButtons = (
    <>
      <button
        type="submit"
        disabled={saving || !title.trim()}
        className="h-8 px-2.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-xs font-semibold disabled:opacity-40"
      >
        {saving ? '…' : submitLabel}
      </button>
      {!persistent && (
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="h-8 px-2 rounded-lg text-xs font-semibold text-gray-500 hover:bg-white/80 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
      )}
    </>
  );

  if (stacked) {
    return (
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className={`flex flex-col gap-2 min-w-0 ${
          compact
            ? (flush ? 'px-3 py-2' : 'px-3.5 py-2 pl-12')
            : 'px-3.5 py-2.5 bg-orange-50/70 dark:bg-gray-700/40'
        }`}
      >
        <input
          ref={inputRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={placeholder}
          disabled={saving}
          className="w-full h-8 px-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
        />
        <div className="flex items-center gap-2 min-w-0">
          {timeSelect}
          {assigneeSelect}
          {spaceSelect}
          {actionButtons}
        </div>
      </form>
    );
  }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className={`flex flex-nowrap items-center gap-2 min-w-0 ${
        compact
          ? (flush ? 'min-h-8 px-3 py-1' : 'min-h-8 px-3.5 pl-12 py-1')
          : 'px-3.5 py-2.5 bg-orange-50/70 dark:bg-gray-700/40'
      }`}
    >
      <input
        ref={inputRef}
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={placeholder}
        disabled={saving}
        className="min-w-0 flex-1 h-8 px-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
      />
      <div className="flex-shrink-0 max-w-[7.5rem]">{timeSelect}</div>
      {assigneeSelect ? (
        <div className="flex-shrink-0 max-w-[8.5rem]">{assigneeSelect}</div>
      ) : null}
      {spaceSelect ? (
        <div className="flex-shrink-0 max-w-[8.5rem]">{spaceSelect}</div>
      ) : null}
      {actionButtons}
    </form>
  );
};

export default QuickTaskComposer;
